<template>
  <div class="dropdown-container" ref="dropdownRef">
    <div class="dropdown" @click="toggleModal">
      <span>{{ selectedOption }}</span>
      <span class="arrow">&#9662;</span>
    </div>

    <div v-show="isModalOpen" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="input-container">
          <IconAtom icon="searchIcon" color="#333" class="search-icon" />
          <InputAtom
            class="search-input"
            placeholder="Search..."
            v-model="searchQuery"
          />
        </div>
        <ul class="options-list" ref="optionsListRef">
          <li :key="'everyone'" class="option-item">
            <label class="checkbox-label">
              <input
                type="checkbox"
                class="checkbox-input"
                :checked="isEveryoneSelected"
                @change="onEveryoneChange($event)"
              />
              <span class="custom-checkbox"></span>
              <span class="checkbox-label"></span>
            </label>
            <span>{{ 'Everyone' }}</span>
          </li>
          <li :key="'premium'" class="option-item">
            <label class="checkbox-label">
              <input
                type="checkbox"
                class="checkbox-input"
                :checked="isPremiumSelected"
                @change="onPremiumChange($event)"
              />
              <span class="custom-checkbox"></span>
              <span class="checkbox-label"></span>
            </label>
            <span>{{ 'Premium users' }}</span>
          </li>
          <li v-for="option in users" :key="option.user_id" class="option-item">
            <label>
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  class="checkbox-input"
                  :value="option"
                  :checked="
                    selectedOptions.some((u) => u.user_id === option.user_id) ||
                    isEveryoneSelected
                  "
                  @change="onUserOptionChange($event, option)"
                />
                <span class="custom-checkbox"></span>
                <span class="checkbox-label"></span>
              </label>
              <img
                :src="option.user_photo ? option.user_photo : userDefaultImage"
                alt=""
                class="avatar"
              />
              <div class="user-info-wrapper">
                <span class="full-name">{{ option.full_name }}</span>
                <span v-show="option.username" class="username">
                  @{{ option.username }}
                </span>
              </div>
            </label>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import {
    ref,
    computed,
    watch,
    onMounted,
    onUnmounted,
    useTemplateRef,
  } from 'vue';
  import IconAtom from '@/components/atoms/IconAtom.vue';
  import InputAtom from '@/components/atoms/InputAtom.vue';
  import { useStore } from 'vuex';
  import userDefaultImage from '@/assets/default-user-img.png';
  import { FilterParams } from '@/shared/filterParams';
  import User from '@/models/user/user.model';
  import { useInfiniteScroll } from '@vueuse/core';

  const isModalOpen = ref(false);
  const searchQuery = ref('');
  const isEveryoneSelected = ref(false);
  const isPremiumSelected = ref(false);
  const selectedOptions = ref<User[]>([]);
  const optionsListRef = useTemplateRef<HTMLElement>('optionsListRef');
  const dropdownRef = useTemplateRef<HTMLElement>('dropdownRef');

  const store = useStore();
  const props = defineProps({
    modelValue: Array,
  });

  const initialFilterParams: FilterParams = {
    page: 1,
    pageSize: 10,
    searchParamsOR: { searchTerm: '' },
    searchParamsAND: [
      { searchTerm: 'active', searchField: 'user_status', action: 'equals' },
    ],
  };
  const resetFilters = () => (filters.value = { ...initialFilterParams });
  const filters = ref<FilterParams>({ ...initialFilterParams });

  const users = computed(() => store.getters['user/getItems']);
  const notification = computed(
    () => store.getters['notification/getNotification'],
  );
  const totalPages = computed(() => store.getters['user/getTotalPages']);

  const initializeSelectedOptions = (sendTo = props.modelValue) => {
    if (sendTo && typeof sendTo[0] === 'object' && sendTo[0].user_id) {
      sendTo = sendTo.map((item) => item.user_id);
    }

    if (notification.value?.send_to_all) {
      isEveryoneSelected.value = true;
      selectedOptions.value = [];
    } else if (sendTo?.includes('Everyone')) {
      isEveryoneSelected.value = true;
      selectedOptions.value = [];
    } else {
      isEveryoneSelected.value = false;
      selectedOptions.value = users.value.filter((user) =>
        sendTo?.includes(user.user_id),
      );
    }

    isPremiumSelected.value = notification.value?.send_to_premium || false;
  };

  onMounted(() => {
    store.dispatch('user/fetchUsers', filters.value);
    initializeSelectedOptions();
  });

  watch(
    () => props.modelValue,
    (newSendTo) => {
      initializeSelectedOptions(newSendTo);
    },
    { immediate: true },
  );

  watch(searchQuery, (newValue) => {
    resetFilters();
    filters.value.searchParamsOR.searchTerm = newValue;
    store.dispatch('user/fetchUsers', filters.value);
  });

  const selectedOption = computed(() => {
    if (isEveryoneSelected.value && isPremiumSelected.value) {
      return 'Everyone, Premium users';
    }
    if (isEveryoneSelected.value) {
      return 'Everyone';
    }
    if (isPremiumSelected.value) {
      return 'Premium users';
    }
    if (selectedOptions.value.length === 1) {
      return selectedOptions.value[0].full_name;
    }
    return `${selectedOptions.value.length} selected`;
  });

  const toggleModal = () => {
    isModalOpen.value = !isModalOpen.value;
    if (isModalOpen.value) {
      setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 0);
    } else {
      document.removeEventListener('click', handleClickOutside);
    }
  };

  const closeModal = () => {
    isModalOpen.value = false;
    document.removeEventListener('click', handleClickOutside);
  };

  const handleClickOutside = (event: Event) => {
    if (
      dropdownRef.value &&
      !dropdownRef.value.contains(event.target as Node)
    ) {
      closeModal();
    }
  };

  const onEveryoneChange = (event: Event) => {
    const target = event.target as HTMLInputElement;
    isEveryoneSelected.value = target.checked;

    if (target.checked) {
      store.commit('notification/SET_NOTIFICATION', {
        ...notification.value,
        send_to_all: true,
        send_to: [],
      });
      selectedOptions.value = [];
    } else {
      store.commit('notification/SET_NOTIFICATION', {
        ...notification.value,
        send_to_all: false,
        send_to: [],
      });
    }
  };

  const onPremiumChange = (event: Event) => {
    const target = event.target as HTMLInputElement;
    isPremiumSelected.value = target.checked;

    store.commit('notification/SET_NOTIFICATION', {
      ...notification.value,
      send_to_premium: target.checked,
    });
  };

  const onUserOptionChange = (event: Event, user: User) => {
    const target = event.target as HTMLInputElement;

    if (target.checked) {
      if (!selectedOptions.value.includes(user)) {
        selectedOptions.value.push(user);
      }
    } else {
      selectedOptions.value = selectedOptions.value.filter(
        (option) => option !== user,
      );
    }

    if (selectedOptions.value.length > 0) {
      isEveryoneSelected.value = false;
      const userIds = selectedOptions.value.map((option) => option.user_id);
      store.commit('notification/SET_NOTIFICATION', {
        ...notification.value,
        send_to_all: false,
        send_to: userIds,
      });
    } else {
      store.commit('notification/SET_NOTIFICATION', {
        ...notification.value,
        send_to_all: false,
        send_to: [],
      });
    }
  };

  const { reset } = useInfiniteScroll(
    optionsListRef,
    () => {
      filters.value.page += 1;
      store.dispatch('user/fetchMoreUsers', filters.value);
    },
    {
      distance: 5,
      canLoadMore: () => filters.value.page < totalPages.value,
    },
  );

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
  });
</script>

<style scoped>
  .input-container {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .search-icon {
    position: absolute;
    left: 10px;
    top: 8px;
  }

  .dropdown-container {
    position: relative;
    width: 100%;
  }

  .dropdown {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: var(--color-gray-30);
    padding: 10px;
    border-radius: 8px;
    cursor: pointer;
  }

  .modal-overlay {
    position: absolute;
    width: 100%;
    transform: translateY(10px);
    padding-bottom: 24px;
  }

  .modal-content {
    background: white;
    padding: 12px;
    border-radius: 10px;
    width: 100%;
    box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
  }

  .search-input {
    background-color: var(--color-gray-30);
    width: 100%;
    padding: 12px;
    margin-bottom: 10px;
    border: 0px solid #ccc;
    border-radius: 15px;
    padding-left: 40px;
  }

  .options-list {
    list-style-type: none;
    padding: 0;
    margin: 0;
    max-height: 280px;
    overflow-y: scroll;
  }

  .option-item {
    display: flex;
    align-items: center;
    padding: 8px;
    height: 60px;
    position: relative;
  }

  .option-item label {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  .option-item::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 1px;
    background-color: var(--color-gray-50);
  }

  .option-item:last-child::after {
    display: none;
  }

  .avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    aspect-ratio: 1/1;
  }

  .arrow {
    font-size: 15px;
  }

  .checkbox-input {
    display: none;
  }

  .custom-checkbox {
    width: 20px;
    height: 20px;
    border-radius: 4px;
    border: 2px solid var(--color-gray-50);
    position: relative;
    cursor: pointer;
    transition: background-color 0.3s, border-color 0.3s;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .checkbox-input:checked + .custom-checkbox::after {
    content: '✔';
    font-size: 14px;
    color: white;
  }

  .checkbox-input:checked + .custom-checkbox {
    background-color: var(--color-black);
    border-color: var(--color-black);
  }

  .custom-checkbox:hover {
    border-color: var(--color-gray-50);
    background-color: var(--color-gray-50);
  }

  .checkbox-label {
    font-size: 12px;
    font-weight: 500;
    color: var(--color-text-dark);
    cursor: pointer;
  }

  .user-info-wrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }

  .user-info-wrapper .full-name {
    font-weight: 500;
    font-size: 18px;
  }

  .user-info-wrapper .username {
    font-weight: 300;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.6);
  }
</style>
